import { useCallback, useEffect, useMemo, useState } from 'react';
import ApiService from '../../../../service/ApiService';
import { apiPaths } from '../../../../service/apiPaths';

export interface BdeEodItem {
  _id?: string;
  user?: string | { _id?: string; name?: string };
  emailSent: number;
  emailReplies: number;
  coldCallsMade: number;
  callConversations: number;
  linkedinReplies: number;
  meetingsScheduled: number;
  followUpsDone: number;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
}

export type BdeEodForm = Omit<BdeEodItem, '_id' | 'user' | 'createdAt' | 'updatedAt'>;

const initialForm: BdeEodForm = {
  emailSent: 0,
  emailReplies: 0,
  coldCallsMade: 0,
  callConversations: 0,
  linkedinReplies: 0,
  meetingsScheduled: 0,
  followUpsDone: 0,
  notes: '',
};

const useBdeForm = () => {
  const [items, setItems] = useState<BdeEodItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<BdeEodForm>(initialForm);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Pagination state
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const PAGE_SIZE = 10;

  const resetForm = useCallback(() => {
    setForm(initialForm);
    setEditingId(null);
  }, []);

  const fetchAll = useCallback(async (requestedPage: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const role = localStorage.getItem('role');
      const isAdminLike = role === 'admin' || role === 'bde-manager' ||role === 'bde' || role === 'hr' || role === 'tl';
      const baseEndpoint = isAdminLike ? apiPaths.bdeFormGetAll : apiPaths.bdeFormGet;
      const endpoint = `${baseEndpoint}?page=${requestedPage}&limit=${PAGE_SIZE}`;
      const res = await ApiService({ method: 'GET', endpoint });
      // Normalize response: API may return array or an object with `bdeForm`/`data.bdeForm`
      const extracted = Array.isArray(res)
        ? res
        : Array.isArray((res as any)?.bdeForm)
          ? (res as any).bdeForm
          : Array.isArray((res as any)?.data?.bdeForm)
            ? (res as any).data.bdeForm
            : Array.isArray((res as any)?.data)
              ? (res as any).data
              : [];
      // Extract pagination meta if present
      const metaSource: any = (res as any)?.data || res || {};
      const currentPageFromApi: number = Number(metaSource.currentPage) || requestedPage;
      const totalItemsFromApi: number = Number(metaSource.totalItems) || extracted.length;
      const totalPagesFromApi: number = Number(metaSource.totalPages) || Math.max(1, Math.ceil(totalItemsFromApi / PAGE_SIZE));
      // Ensure fields are search-friendly for the table (e.g., `user` should be a string)
      const normalized: BdeEodItem[] = extracted.map((item: any) => {
        const userField = item?.user;
        const userName = typeof userField === 'object' && userField !== null
          ? (userField.name || '')
          : (userField || '');
        return {
          ...item,
          user: userName,
        } as BdeEodItem;
      });
      setItems(normalized);
      setPage(currentPageFromApi);
      setTotalItems(totalItemsFromApi);
      setTotalPages(totalPagesFromApi);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll(1);
  }, [fetchAll]);

  const create = useCallback(async (payload: BdeEodForm) => {
    setLoading(true);
    setError(null);
    try {
      await ApiService({ method: 'POST', endpoint: apiPaths.bdeFormCreate, data: payload });
      await fetchAll();
      resetForm();
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to create');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchAll, resetForm]);

  const update = useCallback(async (id: string, payload: BdeEodForm) => {
    setLoading(true);
    setError(null);
    try {
      await ApiService({ method: 'PUT', endpoint: `${apiPaths.bdeFormEdit}${id}` , data: payload });
      await fetchAll();
      resetForm();
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to update');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchAll, resetForm]);

  const remove = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await ApiService({ method: 'DELETE', endpoint: `${apiPaths.bdeFormDelete}${id}` });
      await fetchAll();
      return true;
    } catch (e: any) {
      setError(e?.message || 'Failed to delete');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchAll]);

  const beginEdit = useCallback((item: BdeEodItem) => {
    setEditingId(item._id || null);
    const { emailSent, emailReplies, coldCallsMade, callConversations, linkedinReplies, meetingsScheduled, followUpsDone, notes } = item;
    setForm({
      emailSent: emailSent || 0,
      emailReplies: emailReplies || 0,
      coldCallsMade: coldCallsMade || 0,
      callConversations: callConversations || 0,
      linkedinReplies: linkedinReplies || 0,
      meetingsScheduled: meetingsScheduled || 0,
      followUpsDone: followUpsDone || 0,
      notes: notes || '',
    });
  }, []);

  const submit = useCallback(async () => {
    if (editingId) {
      return update(editingId, form);
    }
    return create(form);
  }, [editingId, update, create, form]);

  const setField = useCallback((field: keyof BdeEodForm, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  return {
    items,
    loading,
    error,
    form,
    setField,
    resetForm,
    editingId,
    beginEdit,
    create,
    update,
    remove,
    submit,
    refetch: fetchAll,
    page,
    totalPages,
    totalItems,
    onPageChange: (p: number) => fetchAll(Math.max(1, Math.min(totalPages || 1, p))),
  };
};

export default useBdeForm;
